import React, { useState } from "react";
import moment from "moment/moment";
import { useMutation } from "@apollo/client/react";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { serverFormat } from "../../../utils/dates";
import { DELETE_INVENTORY_REQUEST_MUTATION } from "../../../constants/graphql/mutations";
import OutboundActions from "./OutboundActions";
import OutboundRowMobile from "./OutboundRowMobile";
import OrderStatusChip from "../orders/OrderStatusChip";
import MovewareJobIdCell from "src/components/integrations/moveware/MovewareJobIdCell";
import useNotification from "src/hooks/notification";

export default function OutboundTable({
  list,
  refetch,
  page,
  totalPages,
  totalCount,
  onPageChange,
  showStartDate = false,
  showDeliveryDate = false,
  showOrderComments = false,
  showProjectName = false,
  showOutboundWeight = false,
  showBillingCharge = false,
  headerAction = null,
  compactActions = false,
  showMovewareDetails = false,
}) {
  const [removing, setRemoving] = useState(false);
  const [selected, setSelected] = useState(null);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [deleteInventoryRequest] = useMutation(DELETE_INVENTORY_REQUEST_MUTATION);
  const { notifySuccess } = useNotification();

  const handleDelete = item => {
    setRemoving(true);
    return deleteInventoryRequest({
      variables: { id: item.id },
    }).then(({ data }) => {
      setRemoving(false);
      if (data?.deleteInventoryRequest?.status === "QUEUED") {
        // Large orders are deleted in the background; a notification follows when it finishes.
        notifySuccess(
          `Order #${item.workOrderNumber} is being deleted. You'll get a notification when it's done.`
        );
      }
      if (refetch) {
        refetch();
      }
    });
  };

  const handleRowClick = itemId => {
    setSelected(itemId);
  };

  const formatOutboundWeight = outboundWeight => {
    if (outboundWeight == null || outboundWeight === "") return "-";

    return `${Number(outboundWeight).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} lbs`;
  };

  const billingChargeTotal = item =>
    item.billingCharges?.grand_total ??
    item.billingCharges?.grandTotal ??
    item.outboundCharges?.total;

  const formatBillingCharge = item => {
    const total = billingChargeTotal(item);

    if (total == null || total === "") return "-";

    return Number(total).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  };

  const renderBillingChargeColumn =
    showBillingCharge && list.some(item => Number(billingChargeTotal(item) || 0) !== 0);

  if (list.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          No Outbound Orders Found
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box height={3}>{removing && <LinearProgress />}</Box>
      <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
        {onPageChange && (
          <Typography color="textSecondary" variant="body2">
            {totalCount} Orders Found. Page {page} of {totalPages}
          </Typography>
        )}
        {headerAction}
      </Box>

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {list.map(item => (
            <OutboundRowMobile
              key={`mobile-${item.id}`}
              item={item}
              onRemove={() => handleDelete(item)}
              refetch={refetch}
              showStartDate={showStartDate}
              showDeliveryDate={showDeliveryDate}
              showOrderComments={showOrderComments}
              showProjectName={showProjectName}
              showOutboundWeight={showOutboundWeight}
              showBillingCharge={renderBillingChargeColumn}
              showMovewareDetails={showMovewareDetails}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
              "& .MuiTableCell-root": {
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {showProjectName && <TableCell align="center">Project Name</TableCell>}
                <TableCell align="center">Order #</TableCell>
                <TableCell align="center">Submitted On</TableCell>
                <TableCell align="center" sx={{ width: "22%" }}>
                  Submitted By
                </TableCell>
                {showMovewareDetails && <TableCell align="center">Moveware Details</TableCell>}
                {showStartDate && <TableCell align="center">Start Date</TableCell>}
                {showDeliveryDate && <TableCell align="center">Delivery Date</TableCell>}
                {showOutboundWeight && <TableCell align="center">Outbound Weight</TableCell>}
                {renderBillingChargeColumn && <TableCell align="center">Billing Charge</TableCell>}
                {showOrderComments && (
                  <TableCell align="center" sx={{ width: "20%" }}>
                    Comments
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={compactActions ? { width: 64, px: 0.5, whiteSpace: "nowrap" } : { width: 112 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map(item => (
                <TableRow
                  key={item.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  hover
                  selected={selected === item.id}
                  onClick={() => handleRowClick(item.id)}
                >
                  {showProjectName && (
                    <TableCell align="center">{item.projectName || "-"}</TableCell>
                  )}
                  <TableCell align="center">
                    {item.workOrderNumber}
                    {item.revisionNumber && item.revisionNumber !== "0"
                      ? `.${item.revisionNumber}`
                      : ""}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                        {moment(item.requestedAt, serverFormat).format("ll")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {moment(item.requestedAt, serverFormat).format("h:mm A")}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "22%" }}>
                    {item.user.email}
                  </TableCell>
                  {showMovewareDetails && (
                    <TableCell align="center">
                      <MovewareJobIdCell item={item} />
                    </TableCell>
                  )}
                  {showStartDate && (
                    <TableCell align="center">
                      {item.startDate ? moment(item.startDate, serverFormat).format("ll") : "-"}
                    </TableCell>
                  )}
                  {showDeliveryDate && (
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.75}>
                        <span>
                          {item.deliveryDate
                            ? moment(item.deliveryDate, serverFormat).format("ll")
                            : "-"}
                        </span>
                        <OrderStatusChip vaStatus={item.vaStatus} />
                      </Box>
                    </TableCell>
                  )}
                  {showOutboundWeight && (
                    <TableCell align="center">
                      {formatOutboundWeight(item.effectiveOutboundWeight || item.outboundWeight)}
                    </TableCell>
                  )}
                  {renderBillingChargeColumn && (
                    <TableCell align="center">{formatBillingCharge(item)}</TableCell>
                  )}
                  {showOrderComments && (
                    <TableCell align="center" sx={{ width: "20%" }}>
                      {item.comment || "-"}
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={compactActions ? { width: 64, px: 0.5 } : { width: 112 }}
                  >
                    <Box
                      display="flex"
                      flexDirection={compactActions ? "column" : "row"}
                      alignItems="center"
                    >
                      <OutboundActions
                        item={item}
                        onRemove={() => handleDelete(item)}
                        refetch={refetch}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {onPageChange && (
        <Box mb={2} mt={2} display="flex" justifyContent="center">
          {totalPages > 1 && (
            <Pagination color="primary" count={totalPages} page={page} onChange={onPageChange} />
          )}
        </Box>
      )}
    </>
  );
}
